import * as React from 'react';

import {
  Table as ShadcnTable,
  TableHeader as ShadcnTableHeader,
  TableBody as ShadcnTableBody,
  TableFooter as ShadcnTableFooter,
  TableHead as ShadcnTableHead,
  TableRow as ShadcnTableRow,
  TableCell as ShadcnTableCell,
  TableCaption as ShadcnTableCaption,
  type TableProps as ShadcnTableProps,
} from '@/hen-ui/shadcn/table';
import { cn } from '@/hen-ui/utils';

export interface TableProps extends ShadcnTableProps {
  children?: React.ReactNode;
  /**
   * Wraps the table in a bordered container with medium info-box padding
   * (`px-info-card-med-sides` / `py-info-card-med-top-bottom`) and
   * `rounded-lg` corners.
   *
   * When `showBorder` is true, `className` is applied to the outer wrapper
   * (not the inner `<table>`). When false, `className` is forwarded to the
   * table element as usual.
   */
  showBorder?: boolean;
  /**
   * Hide the header row and render each cell as a labeled block element
   * below 768 px, matching the mobile stacking behavior of `.sonic-table`.
   *
   * Defaults to `true` when rendered via the `[hen-ui-table]` WordPress shortcode.
   */
  stackOnMobile?: boolean;
}

/**
 * Table component following HEN Design System standards.
 *
 * A thin wrapper around shadcn/ui table components with HEN-specific design.
 * All styling uses tokens from tailwind.config.ts.
 *
 * @example
 * <Table showBorder>
 *   <TableCaption>A list of recent invoices.</TableCaption>
 *   <TableHeader>
 *     <TableRow>
 *       <TableHead>Invoice</TableHead>
 *       <TableHead>Status</TableHead>
 *     </TableRow>
 *   </TableHeader>
 *   <TableBody>
 *     <TableRow>
 *       <TableCell>INV001</TableCell>
 *       <TableCell>Paid</TableCell>
 *     </TableRow>
 *   </TableBody>
 * </Table>
 */
export const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ showBorder = false, className, noWrapper, ...props }, ref) => {
    if (showBorder) {
      return (
        <div
          className={cn(
            'relative w-full overflow-auto rounded-lg border border-neutral-200 bg-white',
            'px-info-card-med-sides py-info-card-med-top-bottom',
            className,
          )}
        >
          <ShadcnTable ref={ref} noWrapper {...props} />
        </div>
      );
    }

    return (
      <ShadcnTable
        ref={ref}
        className={className}
        noWrapper={noWrapper}
        {...props}
      />
    );
  },
);
Table.displayName = 'Table';

export const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>((props, ref) => <ShadcnTableHeader ref={ref} {...props} />);
TableHeader.displayName = 'TableHeader';

export const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>((props, ref) => <ShadcnTableBody ref={ref} {...props} />);
TableBody.displayName = 'TableBody';

export const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>((props, ref) => <ShadcnTableFooter ref={ref} {...props} />);
TableFooter.displayName = 'TableFooter';

export const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>((props, ref) => <ShadcnTableHead ref={ref} {...props} />);
TableHead.displayName = 'TableHead';

export const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>((props, ref) => <ShadcnTableRow ref={ref} {...props} />);
TableRow.displayName = 'TableRow';

export const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>((props, ref) => <ShadcnTableCell ref={ref} {...props} />);
TableCell.displayName = 'TableCell';

export const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>((props, ref) => <ShadcnTableCaption ref={ref} {...props} />);
TableCaption.displayName = 'TableCaption';
